import React, { useEffect, useRef, useState } from "react";
import CustomImage from "../../atoms/customImage";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/InnerImageZoom/styles.css";
import { useSelector } from "react-redux";
import ButtonComponent from "../../atoms/button";
import Typography from "../../atoms/typography";
import PropType from "prop-types";
import { constants, PRODUCT_VIDEO_POSITION } from "../../../constants/pageConstants/pdpConstants";
import isEmpty from "lodash.isempty";
import VideoPlayer from "../../atoms/videoPlayer";

/**
 * Renders the ZoomGalleryBody component that manages tooltip visibility and handles image click events.
 *
 * @param {Function} props.imageClick - The callback function triggered when an image is clicked.
 *
 * @returns {JSX.Element} The ZoomGalleryBody component with tooltip management and image click handling.
 */
const ZoomGalleryBody = ({ imageClick, productVideoLink, activeIndex }) => {
  const videoRef = useRef();
  const [playVideo, setPlayVideo] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const isMobile = useSelector((state) => state.appConfig.isMobile);
  const isVideoExists = !isEmpty(productVideoLink) && activeIndex === PRODUCT_VIDEO_POSITION;
  const isYoutube = productVideoLink?.indexOf("youtube") > -1;

  useEffect(() => {
    if (!sessionStorage.getItem("touchIntro") && isMobile) {
      setTimeout(() => {
        setShowTooltip(true);
      }, 500);
    }
  }, []);

  useEffect(() => {
    if (isVideoExists) {
      setPlayVideo(false);
    }
  }, [isVideoExists]);

  /**
   * Handles the "Got It" action for dismissing the tooltip.
   *
   * This function sets a flag in `sessionStorage` to track that the tooltip introductionp.
   */
  const onHandleGotIt = () => {
    sessionStorage.setItem("touchIntro", false);

    setShowTooltip(false);
  };

  /**
   *
   */
  const onPlayingHandler = (event, isSlideAutoPlay) => {
    event.stopPropagation();
    if (videoRef?.current) {
      if (isSlideAutoPlay) {
        setPlayVideo(true);
        videoRef.current.controls = true;
        videoRef.current?.play();
      } else {
        setPlayVideo(false);
        videoRef.current.controls = false;
        videoRef.current?.pause();
      }
    }
  };

  /**
   * Renders a CustomImage component for displaying a play/pause icon.
   * @param {string} src - The source URL of the play/pause icon image.
   * @returns {JSX.Element} The CustomImage component with the specified source URL and styles.
   */
  const renderPlayPauseIcon = (src, width = 40, height = 40) => {
    return (
      <CustomImage
        src={src}
        alt={`${playVideo ? "Pause" : "Play"}`}
        width={width}
        height={height}
        sizes="10vw"
        isPriority={false}
        itemProp="image"
        className="m-auto"
      />
    );
  };

  return (
    <div className={`modalGallery relative ${isVideoExists ? "h-[calc(100%-35vh)]" : "h-auto"} md:h-[100%]`}>
      {showTooltip && (
        <div className="text-white fixed inset-0 z-10 flex flex-col items-center justify-center bg-black-100 bg-opacity-70">
          <Typography variant="p" Tag="p" className="mb-2 text-lg font-bold text-white-900 ">
            {constants.TAP_TO_ZOOM}
          </Typography>

          <ButtonComponent
            onClick={onHandleGotIt}
            labelStyle="text-fnp-300 "
            label="Got It"
            size=""
            buttonVariant="filled"
            isRemoveExtraPadding={true}
            classes="bg-white-900 text-black-900 w-[75px]  py-[8px] border my-8 rounded-full"
            backGroundColor="bg-white"
          />

          <CustomImage src="/icons/tapToZoomFinger.svg" alt="cross icons" sizes="10vw" width={18} height={18} />
        </div>
      )}

      {isVideoExists ? (
        isYoutube ? (
          <iframe
            border="0"
            src={`${productVideoLink}?autoplay=1&modestbranding=1&showinfo=0&rel=0&loop=1`}
            target="_parent"
            height="100%"
            name="Product Video"
            width="100%"
            title="iframe Product Video"
            data-testid="product-video"
            className="h-full w-full rounded-2xl bg-black-900 md:h-[71.85vh]"
          />
        ) : (
          <>
            {!playVideo && (
              <button
                className="absolute z-9 flex h-full w-full justify-center"
                onClick={(e) => onPlayingHandler(e, true)}
              >
                {renderPlayPauseIcon("/icons/play.svg", 60, 60)}
              </button>
            )}
            <VideoPlayer
              customRef={videoRef}
              videoUrl={productVideoLink}
              title="Product Video"
              classes="h-full w-full bg-black-900 rounded-2xl md:h-[71.85vh]"
              isMuted={true}
              onPlaying={onPlayingHandler}
              testId="product-video"
              isPlaysInline={true}
            />
          </>
        )
      ) : (
        <InnerImageZoom
          src={imageClick}
          zoomSrc={imageClick}
          moveType="pan"
          zoomScale={1.3}
          zoomType="click"
          mobileBreakpoint={768}
        />
      )}
    </div>
  );
};

ZoomGalleryBody.propTypes = {
  imageClick: PropType.string,
  productVideoLink: PropType.string,
  activeIndex: PropType.number,
};

export default ZoomGalleryBody;
